/**
 * MessageList — Scrollable message container.
 *
 * Routes each message to the appropriate component:
 * user -> UserMessage, assistant -> AssistantMessage,
 * loading -> ThinkingIndicator, OAuth -> WaitingOAuthBubble.
 *
 * Initial message uses the new icon+name layout (no MessageBubble).
 */

import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { UserMessage } from "./UserMessage";
import { AssistantMessage } from "./AssistantMessage";
import { ThinkingIndicator } from "./ThinkingIndicator";
import WaitingOAuthBubble from "./WaitingOAuthBubble";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import cogsolAvatar from "../assets/cogsol-logo.svg";

const DEFAULT_AVATAR = cogsolAvatar;

const MessageList = forwardRef(
  (
    {
      messages,
      iniMessage,
      assistantColors,
      urlLogo,
      chatName,
      executionStatus,
      waitingOAuthServerId,
      chatId,
      onReauthorize,
      expandedReferences,
      onToggleReferences,
      // File attachments & metadata
      attachments = [],
      messageMetadataList = [],
      allowMetadata = false,
      onFileMetadataClick,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    // Look up attachments for a given message index
    const getFilesForMessage = (message, messageIndex) => {
      if (message.attachments && message.attachments.length > 0) {
        return message.attachments;
      }
      const entry = attachments.find((a) => a.messageIndex === messageIndex);
      return entry ? entry.files : [];
    };

    // Look up metadata for a given message index
    const getMetadataForMessage = (message, messageIndex) => {
      if (
        message.metadata &&
        typeof message.metadata === "object" &&
        Object.keys(message.metadata).length > 0
      ) {
        return message.metadata;
      }
      const entry = messageMetadataList.find(
        (m) => m.messageIndex === messageIndex,
      );
      return entry ? entry.metadata : null;
    };

    const getFileMetadataMap = (message) => {
      const raw = message.metadata_files || {};
      const parsed = {};
      for (const [key, val] of Object.entries(raw)) {
        if (typeof val === "string") {
          try { parsed[key] = JSON.parse(val); } catch { parsed[key] = val; }
        } else {
          parsed[key] = val;
        }
      }
      return parsed;
    };

    return (
      <>
        {/* Initial message — new icon+name layout */}
        {iniMessage != null && iniMessage !== "" && (
          <div className="flex flex-col gap-0.5 my-6">
            <div className="flex items-center gap-1.5 mb-1">
              <img
                src={urlLogo || DEFAULT_AVATAR}
                alt={chatName || t("assistant")}
                className="w-8 h-8 rounded-md object-cover shrink-0"
                onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
              />
              <span
                className="text-xs font-semibold truncate max-w-[200px]"
                style={{ color: assistantColors?.nameColor || "#111827" }}
              >
                {chatName || t("assistant")}
              </span>
            </div>
            <Markdown
              className="text-[15px] leading-relaxed font-sans"
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <p className="my-2">{children}</p>,
                ol: ({ children }) => <ol className="list-decimal pl-6 my-2 space-y-1.5">{children}</ol>,
                ul: ({ children }) => <ul className="list-disc pl-6 my-2 space-y-1.5">{children}</ul>,
                li: ({ children }) => <li className="my-1">{children}</li>,
                a: ({ children, ...aProps }) => (
                  <a target="_blank" rel="noopener noreferrer" className="text-blue-800 underline" {...aProps}>
                    {children}
                  </a>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-gray-300 pl-4 my-2 italic text-gray-600">{children}</blockquote>
                ),
              }}
            >
              {iniMessage}
            </Markdown>
          </div>
        )}

        {/* Message list */}
        {messages.map((message, index) => {
          if (message.role === "user") {
            return (
              <UserMessage
                key={index}
                message={message}
                messageIndex={index}
                assistantColors={assistantColors}
                files={getFilesForMessage(message, index)}
                fileMetadataMap={getFileMetadataMap(message)}
                metadata={getMetadataForMessage(message, index)}
                allowMetadata={allowMetadata}
                onFileMetadataClick={onFileMetadataClick}
              />
            );
          }

          // OAuth waiting bubble
          if (
            executionStatus === "WAITING_FOR_AUTH" &&
            index === messages.length - 1 &&
            message.role === "assistant" &&
            !message.loading
          ) {
            return (
              <WaitingOAuthBubble
                key={index}
                message={message}
                serverId={waitingOAuthServerId}
                chatId={chatId}
                onReauthorize={onReauthorize}
                assistantColors={assistantColors}
                urlLogo={urlLogo}
                chatName={chatName}
              />
            );
          }

          // Loading/thinking indicator
          if (message.loading) {
            return (
              <ThinkingIndicator
                key={index}
                assistantColors={assistantColors}
                urlLogo={urlLogo}
                chatName={chatName}
              />
            );
          }

          // Assistant message
          return (
            <AssistantMessage
              key={index}
              message={message}
              messageIndex={index}
              chatName={chatName}
              avatarUrl={urlLogo}
              nameColor={assistantColors?.nameColor || "#111827"}
              secondaryColor={assistantColors?.secondaryColor}
              expandedReferences={expandedReferences}
              onToggleReferences={onToggleReferences}
            />
          );
        })}

        {/* Scroll anchor */}
        <div ref={ref} className="h-2" />
      </>
    );
  },
);

MessageList.displayName = "MessageList";

export { MessageList };
